import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

/**
 * Determines whether an element can receive focus.
 * @param el - The element to check
 * @returns `true` if the element has a `focus` method, `false` otherwise
 */
const isFocusable = (el: any): boolean => !!el && typeof el.focus === 'function';

/**
 * Programmatically controls focus for HTML elements with built-in throttling.
 *
 * @remarks
 * This service manages element focus through a throttled subject to prevent excessive
 * focus operations. It automatically validates that elements are focusable before
 * applying focus. The service is provided at the root level and automatically cleans
 * up subscriptions when destroyed.
 *
 * Typically used indirectly through the {@link FocusDirective} for declarative focus
 * control in templates, or injected directly for programmatic focus management.
 *
 * @example
 *
 * ```typescript
 * import { inject, Component, ViewChild, ElementRef } from '@angular/core';
 * import { FocusService } from '@backbase/ui-ang/focus';
 *
 * @Component({
 *   selector: 'bb-search-input',
 *   template: `
 *     <input #searchInput type="text" />
 *     <button (click)="focusSearch()">Focus Search</button>
 *   `,
 * })
 * export class SearchInputComponent {
 *   @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
 *   private focusService = inject(FocusService);
 *
 *   focusSearch() {
 *     this.focusService.focus(this.searchInput);
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class FocusService implements OnDestroy {
  private readonly focusSubject = new Subject<ElementRef<HTMLElement>>();
  private readonly sub = this.focusSubject.pipe(throttleTime(0, undefined, { leading: true })).subscribe({
    next: (focused) => {
      if (isFocusable(focused.nativeElement)) {
        focused.nativeElement.focus();
      }
    },
  });

  /**
   * Moves focus to the specified element.
   *
   * @remarks
   * The focus operation is throttled to prevent excessive DOM updates. Only the first
   * focus request within a throttle window (or the leading edge of a new window) is
   * processed. Invalid or non-focusable elements are silently skipped.
   *
   * @param el - {@link ElementRef} wrapping the HTML element to focus
   *
   * @example
   *
   * ```typescript
   * import { Component, inject, ViewChild, ElementRef } from '@angular/core';
   * import { FocusService } from '@backbase/ui-ang/focus';
   *
   * @Component({
   *   selector: 'bb-dialog',
   *   template: `
   *     <div #dialogContent>
   *       <input #firstField type="text" placeholder="First name" />
   *       <button (click)="moveFocusToField()">Focus Input</button>
   *     </div>
   *   `,
   * })
   * export class DialogComponent {
   *   @ViewChild('firstField') firstField!: ElementRef<HTMLInputElement>;
   *   private focusService = inject(FocusService);
   *
   *   moveFocusToField() {
   *     this.focusService.focus(this.firstField);
   *   }
   * }
   * ```
   */
  focus(el: ElementRef<HTMLElement>) {
    this.focusSubject.next(el);
  }

  /**
   * Cleans up the focus service by unsubscribing from the internal subject.
   *
   * @remarks
   * This lifecycle method is automatically invoked by Angular when the service is
   * destroyed. It ensures all subscriptions are properly released to prevent memory leaks.
   */
  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
